import { z } from 'zod';
import { email, isoDate, uuid } from './common.js';

/**
 * Back-office MCP server (issue #47, decision 0030): the OAuth 2.1 wire formats (snake_case,
 * as the RFCs name them) and what the consent page and the settings page show.
 */

const url = z.string().trim().min(1).max(2000);

/** RFC 7591 registration request. Unknown members are ignored, as the RFC asks. */
export const oauthRegistrationInput = z.object({
  redirect_uris: z.array(url).min(1).max(10),
  client_name: z.string().trim().max(200).optional(),
  token_endpoint_auth_method: z.string().max(100).optional(),
  grant_types: z.array(z.string().max(100)).max(10).optional(),
  response_types: z.array(z.string().max(100)).max(10).optional(),
  scope: z.string().max(500).optional(),
});
export type OAuthRegistrationInput = z.infer<typeof oauthRegistrationInput>;

/** `GET /oauth/authorize` query (OAuth 2.1 §4.1.1, PKCE, RFC 8707 `resource`). */
export const oauthAuthorizeQuery = z.object({
  response_type: z.string().max(50),
  client_id: z.string().max(100),
  redirect_uri: url,
  code_challenge: z.string().max(200).optional(),
  code_challenge_method: z.string().max(20).optional(),
  state: z.string().max(2000).optional(),
  scope: z.string().max(500).optional(),
  resource: url.optional(),
});
export type OAuthAuthorizeQuery = z.infer<typeof oauthAuthorizeQuery>;

/** `GET /oauth/requests/:id`: what the consent page shows before the admin approves. */
export const oauthRequestView = z.object({
  id: uuid,
  status: z.enum(['pending', 'approved', 'denied', 'expired']),
  /** Self-asserted at registration: always shown with `redirectHost`. */
  clientName: z.string(),
  redirectHost: z.string(),
  /** Only loopback redirects: a program on this computer, with a warning (MCP spec). */
  loopback: z.boolean(),
  /** Whether this browser started the request: only that one may approve it. */
  sameBrowser: z.boolean(),
  eventName: z.string(),
  expiresAt: isoDate,
});
export type OAuthRequestView = z.infer<typeof oauthRequestView>;

/** Approve or deny: the browser goes back to the client with the code (or the error). */
export const oauthDecisionOutput = z.object({ redirectTo: z.string() });
export type OAuthDecisionOutput = z.infer<typeof oauthDecisionOutput>;

/** A live MCP connection, in the back-office settings (A-13 page). */
export const adminMcpConnection = z.object({
  id: uuid,
  user: z.object({ id: uuid, email }),
  clientName: z.string(),
  redirectHost: z.string(),
  createdAt: isoDate,
  lastUsedAt: isoDate.nullable(),
  expiresAt: isoDate,
});
export type AdminMcpConnection = z.infer<typeof adminMcpConnection>;

export const adminMcpConnectionsOutput = z.object({
  /** The URL to give Claude as a custom connector (the Worker's, also on previews). */
  mcpUrl: z.string(),
  connections: z.array(adminMcpConnection),
});
export type AdminMcpConnectionsOutput = z.infer<typeof adminMcpConnectionsOutput>;

export const adminMcpRevokeOutput = z.object({ revoked: z.number().int() });
export type AdminMcpRevokeOutput = z.infer<typeof adminMcpRevokeOutput>;
